import { Icon, Panel, Tag } from '@dayon/design-system';
import type { IdentityRead } from '../identity/PlayerIdentityRecords.tsx';
import { outcomeWords } from '../identity/record-words.ts';
import { KYC_LABEL } from '../players/vocabulary.ts';
import { KYC_TONE, sentence } from './vocabulary.ts';

/**
 * WHERE THE ACCOUNT STANDS ON IDENTITY, AND WHETHER ITS IDENTITY CHECK SAYS THE SAME.
 *
 * The account carries an identity status of its own — the field the profile's header pill reads,
 * and the one deposits, play and withdrawals are checked against. The identity check on this tab is
 * a separate record. A decision on the check moves the account with it, so the two agree; but an
 * account can carry a status something else set, and a verdict in the older KYC process, before it
 * closed on 23 October 2026, moved an account to Rejected whatever its identity check said. A
 * reviewer reading Verified in the identity record of an account that stays closed needs both on
 * one screen, and the reason they differ.
 *
 * ONE MOMENT'S ANSWER. The profile reads the status once, when the page opens; the identity read
 * runs when this tab opens, and sends the account's status from the same statement as the checks.
 * Once that read has arrived, the line shows its status and the comparison uses nothing else — a
 * decision landing between the two reads would otherwise read as a disagreement the database does
 * not hold. Until it arrives, and for a reader the identity record is withheld from, the line shows
 * the profile's status and compares nothing.
 */

/** The account's status in the console's words — the roster's, the quick look's, the filter's. */
const statusWords = (status: string): string => KYC_LABEL[status] ?? sentence(status);

/**
 * The one sentence for an account whose status its DECIDED identity check contradicts, or null —
 * both read from the one identity read, never the profile's copy.
 *
 * Only a decided check is compared: Verified with Verified, Refused with Rejected. A check still in
 * hand — photographs not sent, with the identity service, waiting for a person — is not an answer
 * yet, and an account can rightly read Rejected while a new attempt is under way. Nothing is compared
 * before the read has loaded, for a reader it is withheld from, for a read that carried no status,
 * or for a player with no current check.
 *
 * Verified against Rejected has one cause the platform can name: nothing on the identity check sets
 * an account to Rejected while the check reads Verified, and a verdict in the older KYC process did.
 * Any other contradiction is reported without a cause this screen cannot know.
 */
export function statusDisagreement(read: IdentityRead): string | null {
  if (read.state !== 'loaded' || read.accountStatus === undefined) return null;
  const status = read.accountStatus;
  const current = read.records.find((r) => r.current);
  if (current === undefined) return null;
  if (current.outcome !== 'VERIFIED' && current.outcome !== 'REFUSED') return null;
  const agreeing = current.outcome === 'VERIFIED' ? 'VERIFIED' : 'REJECTED';
  if (status === agreeing) return null;
  if (current.outcome === 'VERIFIED' && status === 'REJECTED') {
    return 'The identity check reads Verified, but the account reads Rejected: an older KYC verdict set the account, not this check.';
  }
  return (
    `The identity check reads ${outcomeWords(current)}, but the account reads ` +
    `${statusWords(status)}: something other than this check set the account.`
  );
}

/**
 * The labelled line, and below it the sentence when the account and its check disagree.
 * `status` is the profile's copy, shown until the identity read brings its own.
 */
export function AccountIdentityStatus({ status, read }: { status: string; read: IdentityRead }) {
  const shown = read.state === 'loaded' && read.accountStatus !== undefined ? read.accountStatus : status;
  const disagreement = statusDisagreement(read);
  return (
    <Panel>
      <dl style={{ margin: 0 }}>
        <div className="def-row">
          <dt>Identity status on the account</dt>
          <dd>
            <Tag tone={KYC_TONE[shown] ?? 'neutral'}>{statusWords(shown)}</Tag>
          </dd>
        </div>
      </dl>
      {disagreement && (
        <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
          <Icon name="info" />
          <div>{disagreement}</div>
        </div>
      )}
    </Panel>
  );
}
